// ============================================
// FLOW TYPES - NEW COMPONENT ARCHITECTURE
// ============================================

import type { ConditionalDisplayConfig, IframeAspectRatio } from "@repo/shared";
import type {
  AssetClassForecastHorizon,
  FinancialEntryItem,
  FlowField,
  FlowFieldScope,
  FlowStatus,
  LocalizedString,
  ProductAssessmentOutcome,
  ProductCostsSource,
  QuizQuestion,
} from "@repo/shared/types";

// Component types
export type ComponentType =
  | "field" // Regular input field
  | "risk_assessment" // Risk assessment widget
  | "portfolio_display" // Portfolio display widget
  | "portfolio_xray" // Portfolio X-ray widget
  | "asset_allocation" // Asset allocation chart (calculated from product allocations, NOT X-Ray API)
  | "portfolio_forecast" // Portfolio forecast with confidence bounds
  | "historical_return" // Portfolio historical return chart
  | "cost_table" // Cost table widget
  | "cost_chart" // Cost projection chart
  | "portfolio_costs" // Portfolio costs display (from collection config, not cost API)
  | "product_costs" // Product costs display (product costs from products in portfolio)
  | "cost_benefit" // QL-416 — existing vs new portfolio metrics + one-time transition costs (portfolio pages, followups)
  | "goal_input" // Goal input form (goal-level fields only: name, type)
  | "portfolio_input" // Portfolio input form (name, schedule/amounts, model/custom toggle — portfolio pages)
  | "custom_portfolio" // Custom portfolio builder (for portfolio pages with custom portfolio type)
  | "text_block" // Static text/heading
  | "spacer" // Vertical spacing
  | "youtube_video" // YouTube video embed
  | "iframe" // Third-party web page / video / form by https URL (QL-776)
  | "image" // Image display
  | "embed" // Custom HTML embed
  | "modal" // Click-to-open info modal with markdown content
  | "file" // Uploaded document rendered as a download link
  | "client_classification" // MiFID II client classification widget
  | "product_assessment" // Product assessment/knowledge widget
  | "expected_value" // Expected value forecast (works on both global and goal pages)
  | "wealth_plan" // Wealth plan with planned withdrawals (global pages only)
  | "financial_situation" // Financial situation assessment (global pages)
  | "trapets_lookup" // Trapets PEP/Sanction lookup (KYC/AML screening)
  | "sumsub_verification" // Sumsub identity verification (KYC, AML screening, document verification, biometrics)
  | "enin_lookup" // Enin company registry/risk lookup (Company clients only, global pages)
  | "sustainability" // QP-179 — sustainability gate + preferences (global pages, advisor + public)
  | "sustainability_status" // QP-179 — per-portfolio sustainability matching table + deviation decision (portfolio pages)
  | "client_preferences" // QP-224 — client preference yes/no answers (global pages, advisor + public)
  | "quiz" // QL-13 — standalone knowledge quiz gating submit (global pages, advisor + public)
  | "asset_class_forecast" // QL-315 — asset-class mix over time, 100%-normalised stacked bar (global + goal pages)
  | "portfolio_list" // QP-172 — clickable portfolio list with payment details (global + goal pages)
  | "goal_list" // QL-281 — instance-wide goal list with add/edit/delete (global pages, advisor + public)
  | "volatility_chart" // QL-314 — volatility line chart with compliance gate (goal pages, advisor-only)
  | "equity_share_chart" // QL-313 — equity share stacked bar with compliance gate (goal pages, advisor-only)
  | "mandate" // QL-375 — the goal's mandate check (goal + portfolio pages, advisor-only)
  | "mandate_comparison" // QL-375 — follow-up: current vs new mandate (goal pages, advisor-only; group 8)
  | "account_cash_overview" // QL-287 — per-account cash after the flow's trades and transfers (global + goal pages, advisor only)
  | "cash_transfers"; // QL-287 — planned transfers between the client's accounts (global + goal pages, advisor only)

export type PageVisibility = "internal" | "public";

export type PageType = "global" | "goal" | "portfolio";

// Page positions live in @repo/shared/types (shared with the API bucket rules); consumers
// import them from there directly.
import type { GlobalPosition, GoalPosition } from "@repo/shared/types";

// Note: per-component requirement flags (requiresRiskModel / requiresPortfolioCollection /
// etc.) live in the shared module @repo/shared (getComponentRequirements). Do not
// reintroduce a local copy here — it would drift from the single source of truth.

// Conditional display types (ConditionalOperator / ConditionalDisplayConfig)
// live in @repo/shared — shared with the API evaluators. Import them directly
// from "@repo/shared"; do not reintroduce copies here.

// Field types (simplified - no special widgets)
export type FieldType =
  | "text"
  | "number"
  | "dropdown"
  | "textarea"
  | "date"
  // QL-211 — date + time of day, stored as UTC "YYYY-MM-DDTHH:mm:ssZ".
  | "datetime"
  | "email"
  | "phone"
  | "radio"
  | "multiselect"
  | "boolean";

// Option-type helpers (isOptionFieldType, parseMultiselectValue, valueContains)
// live in @repo/shared — shared with the API. Do not reintroduce copies here.
// boolean fields store "true"/"false" strings; a required boolean must be
// ticked ("true") to count as filled (consent-style, like HTML required
// checkboxes).

// FlowField and FlowFieldScope live in @repo/shared/types (the API's response
// contract); import them from there directly. Do not reintroduce copies here.

// Basic models (matching Prisma schema)
export type FlowPage = {
  id: string;
  flowId: string;
  name: string;
  order: number;
  visibility: PageVisibility;
  pageType: PageType;
  globalPosition: GlobalPosition;
  /** QL-117 — goal pages only: before (default) or after the goal's portfolio pages. */
  goalPosition: GoalPosition;
  conditionalVisibility?: ConditionalDisplayConfig | null;
  enableAdvisorNotes?: boolean;
  /** QL-43 — a non-empty advisor note is required before the flow can be submitted. */
  requireAdvisorNotes?: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type FlowPageLayout = {
  id: string;
  flowPageId: string;
  layoutType: string; // "two_column" | "tabs_block" (stored as string in DB)
  order: number;
  config?: string | null; // JSON configuration including conditional display
  createdAt: Date;
  updatedAt: Date;
};

export type ColumnPosition = "left" | "right";

export type FlowPageComponent = {
  id: string;
  flowPageId: string;
  flowPageLayoutId: string | null;
  columnPosition: ColumnPosition | null;
  tabId: string | null; // Tab ID for tabs_block layout (e.g., "tab-1", "tab-2")
  componentType: ComponentType;
  flowFieldId: string | null;
  config: string | null; // JSON configuration
  order: number;
  createdAt: Date;
  updatedAt: Date;
};

export type Flow = {
  id: string;
  name: string;
  description: string | null;
  status: FlowStatus;
  // QL-401 — followup opt-in gate; true implies the flow is active.
  supportedForFollowup: boolean;
  /** QP-184 — which client types may start this flow; null = all. */
  allowedClientTypes?: string[] | null;
  order: number;
  riskModelId: string | null;
  portfolioCollectionId: string | null;
  financialSituationModelId: string | null;
  currency?: string; // Optional, defaults to NOK
  language?: string; // Language for this flow (set at creation, cannot be changed)
  multiGoal?: boolean; // Enable multiple goals per flow instance
  maxGoals?: number; // QL-593 — goal cap per instance while multiGoal is on (1–30)
  multiPortfolio?: boolean; // Enable multiple portfolios per goal
  allowPublicStart?: boolean; // Allow clients to start new flow instances from public link
  allowPublicPdfGen?: boolean; // Allow PDF generation on public completion page
  allowPublicSigning?: boolean; // Allow document signing on public completion page
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
};

export type FlowInstance = {
  id: string;
  flowId: string;
  clientId: string;
  status: string; // "draft" | "in_progress" | "completed"
  startedAt: Date;
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

export type FlowInstanceValue = {
  id: string;
  flowInstanceId: string;
  flowFieldId: string | null;
  goalId: string | null; // For goal-scoped field values
  snapshotType: string | null; // "risk_assessment" | "portfolio" | "xray"
  snapshotKey: string | null;
  value: string;
  createdAt: Date;
  updatedAt: Date;
};

// Risk model types
export type RiskModel = {
  id: string;
  nameI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  isActive: boolean;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
};

export type PortfolioCollection = {
  id: string;
  nameI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  isActive: boolean;
  riskModelId: string;
  createdAt: Date;
  updatedAt: Date;
};

// With relations
export type FlowPageLayoutWithComponents = FlowPageLayout & {
  components: (FlowPageComponent & {
    flowField?: FlowField;
  })[];
};

export type FlowPageWithComponents = FlowPage & {
  components: (FlowPageComponent & {
    flowField?: FlowField;
  })[];
  layouts?: FlowPageLayoutWithComponents[];
};

// Page item union type - for mixed layout rendering
export type PageItem =
  | { type: "layout"; layout: FlowPageLayoutWithComponents }
  | {
      type: "component";
      component: FlowPageComponent & { flowField?: FlowField };
    };

export type FlowWithRelations = Flow & {
  fields: FlowField[];
  pages: FlowPageWithComponents[];
  riskModel?: RiskModel;
  portfolioCollection?: PortfolioCollection;
};

export type FlowInstanceWithRelations = FlowInstance & {
  flow: FlowWithRelations;
  client: {
    id: string;
    name: string;
    email: string;
  };
  values: FlowInstanceValue[];
};

// Component configuration types
export type RiskAssessmentConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type PortfolioDisplayConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  /** QL-741 — a pie chart of the products' weights above the table. Default: false. */
  showChart?: boolean;
  /** QL-741 — the products table. Default: true. */
  showTable?: boolean;
  showPortfolioName?: boolean; // Default: true
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type PortfolioXrayDisplayMode =
  | "asset_allocation"
  | "country_exposure"
  | "regional_exposure"
  | "stock_sector"
  | "equity_style"
  | "bond_sector"
  | "top_holdings";

export type PortfolioXrayConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  displayMode?: PortfolioXrayDisplayMode; // Default: "asset_allocation"
  showChart?: boolean; // Show chart (not applicable for top_holdings), default: true
  showTable?: boolean; // Show table below chart (not applicable for top_holdings), default: false
  /** Show existing-vs-modified comparison in followup sessions (pie chart modes only). Default: false. */
  enableFollowupComparison?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type AssetAllocationConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  showChart?: boolean; // Show pie/donut chart, default: true
  showTable?: boolean; // Show table below chart, default: false
  /** Show existing-vs-modified comparison in followup sessions. Default: false. */
  enableFollowupComparison?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

/**
 * QL-315 — Asset Class Forecast: the asset-class mix over time as a
 * 100%-normalised stacked bar. Global and goal pages; advisor and public flow.
 *
 * Deliberately no `enableFollowupComparison`: the existing portfolio is drawn
 * as the chart's own leading baseline bar whenever the portfolios are
 * follow-ups, not as a second side-by-side view, so there is nothing to opt in
 * to.
 */
export type AssetClassForecastConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  showChart?: boolean; // Show the stacked bar chart, default: true
  showTable?: boolean; // Show the legend table below the chart, default: true
  /**
   * QL-313 — which horizons the toggle offers. Absent = all three (opt-out),
   * so flows saved before the option existed render exactly as before.
   */
  enabledHorizons?: AssetClassForecastHorizon[];
  /** Which toggle position the chart opens on. Default: "10Y". */
  defaultHorizon?: AssetClassForecastHorizon;
  conditionalDisplay?: ConditionalDisplayConfig;
};

/** QL-313 — equity share stacked bar with compliance gate. Same shape as the volatility chart's. */
export type EquityShareChartConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  /** Which horizons the advisor can toggle between. Default: all three. */
  enabledHorizons?: AssetClassForecastHorizon[];
  /** Which toggle position the chart opens on. Default: first enabled. */
  defaultHorizon?: AssetClassForecastHorizon;
  /** Show the legend table below the chart. Default: true. */
  showTable?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

/** QL-314 — volatility line chart with compliance gate. */
export type VolatilityChartConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  /** Which horizons the advisor can toggle between. Default: all three. */
  enabledHorizons?: AssetClassForecastHorizon[];
  /** Which toggle position the chart opens on. Default: first enabled. */
  defaultHorizon?: AssetClassForecastHorizon;
  /** Show a data table of volatility values below the chart. Default: false. */
  showTable?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type PortfolioForecastConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  showStatistics?: boolean; // Expected Return & Volatility display (default: true)
  showTable?: boolean; // Lower/Expected/Upper values display (default: true)
  compareWithBank?: boolean; // Bank-savings comparison line (default: false)
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type HistoricalReturnConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

/**
 * QL-281 — Goal List component: every goal of the instance with edit/delete
 * actions and an "Add goal" modal showing the goal details form. Global pages
 * only; renders on both the advisor wizard and the public flow.
 */
export type GoalListConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  /** Show the optional goal-description textarea in the add/edit modal (max 150 chars). Default false. */
  showDescription?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type TextBlockConfig = {
  // QL-296 — one "Content" field: markdown with a single heading style plus
  // paragraphs; every heading renders as the flow component header.
  content: string; // Markdown content
  /** @deprecated never written since QL-296; `textBlockContent()` folds it into `content`. */
  title?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type SpacerConfig = {
  height: number; // in pixels
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type YoutubeVideoConfig = {
  url: string; // YouTube video URL
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type IframeConfig = {
  url: string; // absolute https URL (validated server-side)
  height?: number; // px, IFRAME_HEIGHT_MIN..MAX; default IFRAME_HEIGHT_DEFAULT; ignored when aspectRatio is set
  aspectRatio?: IframeAspectRatio; // responsive sizing for players (16:9 …) — overrides height
  style?: ComponentStyleType; // platform frame, same vocabulary as embed
  title?: string; // heading; also the iframe's accessible name
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type ImageConfig = {
  url: string; // Image URL (from file upload)
  alt?: string; // Alt text for accessibility
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type EmbedConfig = {
  html: string; // Raw HTML to embed
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type ModalConfig = {
  buttonLabel: string; // Label shown on the inline trigger
  title: string; // Dialog heading
  content: string; // Markdown content (same renderer as text_block)
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type FileConfig = {
  label?: string; // Link text (defaults to fileName)
  s3Key: string; // Storage key of the uploaded document
  fileName: string; // Original file name of the upload
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type FieldComponentConfig = {
  conditionalDisplay?: ConditionalDisplayConfig;
};

// Component style options for universal styling
export type ComponentStyleType = "none" | "border" | "box";

export type ClientClassificationConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type ProductAssessmentConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// QP-150: goal_input now edits goal-level fields only (name, type). Schedule/amount and the
// model/custom toggle moved to portfolio_input (PortfolioInputConfig).
export type GoalInputConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  /** QP-187 — show the optional goal-description textarea (max 150 chars). Default false. */
  showDescription?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// QP-150: portfolio_input edits a portfolio's name, schedule/amounts/dates, and the
// model/custom/dynamic toggle. Carries the allowed-portfolio-types config previously on goal_input.
export type PortfolioInputConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  showPortfolioType?: boolean;
  allowedPortfolioTypes?: ("model" | "custom" | "dynamic")[];
  /**
   * QL-697 — the type a NEW portfolio starts with; one of `allowedPortfolioTypes`.
   * Absent = automatic: model when allowed, else the first allowed type. Applied by
   * the backend on create (`resolveDefaultPortfolioType`); existing portfolios keep
   * their type (the widget's auto-correct for a disallowed type does not read it).
   */
  defaultPortfolioType?: "model" | "custom" | "dynamic";
  /**
   * QL-746 — show the portfolio name field. Default: true. When false the field
   * is hidden and the portfolio keeps the name it was created with.
   */
  showPortfolioName?: boolean;
  showDateInputs?: boolean; // Show one-time date and recurring start/end dates (default: false)
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type CustomPortfolioConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  showIsin?: boolean;
  showProductType?: boolean;
  /** If true, blocks form submission when portfolio contains unsuitable products */
  onlyAllowSuitableProducts?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type PortfolioCostsConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type ProductCostsConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
  /** QL-417 — "existing" lists the frozen holdings on followup portfolios;
   * other portfolios fall back to the new portfolio. Default: "new". */
  portfolioSource?: ProductCostsSource;
};

export type CostTableConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  // QP-208 — optional second "After N years" column (MiFID II holding-period slice)
  showHoldingPeriod?: boolean; // Default: false
  holdingPeriodYears?: number; // Default: 10; capped at the simulated series length
  conditionalDisplay?: ConditionalDisplayConfig;
};

// Union type for all component configs
/**
 * QL-287 — Account Cash Overview: one row per client account with its starting
 * cash, the net cash effect of the flow's portfolios, internal transfers, the
 * deposit needed and the cash left. Read-only. Global and goal pages; the rows
 * are instance-wide, so every placement shows the same figures.
 */
export type AccountCashOverviewConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

/**
 * QL-287 — Cash Transfers: the instance's planned transfers between the
 * client's accounts with add/edit/delete. Global and goal pages; the list is
 * instance-wide, so every placement edits the same transfers.
 */
export type CashTransfersConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type ComponentConfig =
  | RiskAssessmentConfig
  | PortfolioDisplayConfig
  | PortfolioXrayConfig
  | HistoricalReturnConfig
  | GoalListConfig
  | AccountCashOverviewConfig
  | CashTransfersConfig
  | TextBlockConfig
  | SpacerConfig
  | YoutubeVideoConfig
  | IframeConfig
  | ImageConfig
  | EmbedConfig
  | ModalConfig
  | FileConfig
  | FieldComponentConfig
  | ClientClassificationConfig
  | ProductAssessmentConfig
  | GoalInputConfig
  | PortfolioInputConfig
  | CustomPortfolioConfig
  | PortfolioCostsConfig
  | ProductCostsConfig
  | CostTableConfig
  | FinancialSituationConfig
  | SumsubVerificationConfig
  | EninLookupConfig
  | SustainabilityConfig
  | SustainabilityStatusConfig
  | ClientPreferencesConfig
  | QuizConfig
  | PortfolioListConfig;

// Layout type for FlowPageLayout
export type LayoutType = "two_column" | "tabs_block";

// Column styling configuration (for 2-column layouts)
export type ColumnConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
};

// Tab configuration (for tabs_block layouts)
export type TabConfig = {
  id: string; // Unique identifier (e.g., "tab-1", "tab-2")
  name: string; // Display name
  description?: string;
  /** QL-217 — hides this tab and everything in it. */
  conditionalDisplay?: ConditionalDisplayConfig;
};

// Layout configuration type (for 2-column layouts)
export type LayoutConfig = {
  conditionalDisplay?: ConditionalDisplayConfig;
  leftColumn?: ColumnConfig;
  rightColumn?: ColumnConfig;
};

// Tabs block configuration type
export type TabsBlockConfig = {
  conditionalDisplay?: ConditionalDisplayConfig;
  tabs: TabConfig[];
};

// Snapshot types
export type SnapshotType =
  | "risk_assessment"
  | "portfolio"
  | "xray"
  | "historical_return"
  | "cost"
  | "client_classification"
  | "product_assessment"
  | "financial_situation";

export type RiskAssessmentSnapshot = {
  timeHorizonId: string;
  timeHorizonName: string;
  riskLevelId: string;
  riskLevelName: string;
  score: number;
  timestamp: string;
};

export type PortfolioSnapshot = {
  portfolioId: string;
  portfolioName: string;
  riskScore: number;
  products: {
    id: string;
    name: string;
    isin: string | null;
    weight: number;
  }[];
  timestamp: string;
};

export type XraySnapshot = {
  portfolioId: string;
  portfolioName: string;
  assetAllocation: {
    assetClass: string;
    percentage: number;
  }[];
  countryExposure?: {
    country: string;
    percentage: number;
  }[];
  regionalExposure?: {
    region: string;
    percentage: number;
  }[];
  globalStockSector?: {
    sector: string;
    percentage: number;
  }[];
  styleBox?: {
    style: string;
    percentage: number;
  }[];
  globalBondSector?: {
    sector: string;
    percentage: number;
  }[];
  topHoldings?: {
    name: string;
    isin: string;
    country: string;
    sector: string;
    weight: number;
  }[];
  timestamp: string;
};

// Asset allocation snapshot (calculated from product allocations, NOT Morningstar X-Ray API)
export type AssetAllocationSnapshot = {
  portfolioId?: string;
  portfolioName?: string;
  allocations: {
    assetClassId: string;
    assetClassName: string;
    percentage: number;
  }[];
  unclassifiedPct: number;
  timestamp: string;
};

// Portfolio forecast snapshot (projected value with confidence bounds)
export type ForecastEntry = {
  date: string;
  deposit: string;
  return_amount: string;
  value: string;
  withdrawal: string;
};

export type PortfolioForecastSnapshot = {
  expectedReturn: number; // Annual expected return %
  volatility: number; // Annual volatility %
  currency: string;
  forecast: ForecastEntry[]; // Expected value projection
  lower: ForecastEntry[]; // 5th percentile
  upper: ForecastEntry[]; // 95th percentile
  bank?: ForecastEntry[]; // Same deposits at the flat bank rate (absent on old snapshots)
  bankReturnPct?: number; // Rate the bank series was generated with (legend label)
  timestamp: string;
};

export type HistoricalReturnSnapshot = {
  portfolioId: string;
  portfolioName: string;
  currencyIso: string;
  instrumentCount: number;
  totalWeightPct: number;
  timeseries: {
    date: string; // YYYY-MM-DD
    value: number;
  }[];
  timestamp: string;
  // Coverage info for partial data (when not all products have instrument links)
  coverage?: {
    totalProducts: number;
    coveredProducts: number;
    originalWeightPct: number;
  };
};

export type CostCategorySummary = {
  cost_category: string;
  total: number;
};

export type FirstYearCost = {
  depositAmount: number;
  accountValueExCost: number;
  accountValue: number;
  costEffectPct: number;
  returnAmount: number;
  returnAmountPct: number;
  expectedReturnPct?: string;
  totalCost: number;
};

export type YearlyCostEntry = {
  year?: string; // For calendar year view (e.g., "2025")
  period?: string | number; // For rolling view (e.g., 1, 2, ...)
  accountValue: number;
  accountValueExCost: number;
  startDate?: string; // For rolling view (e.g., "2025-11-30")
  endDate?: string; // For rolling view (e.g., "2026-10-31")
  // QP-208 — per-period cost-effect fields so the cost table can fold an
  // "after N years" slice at display time. Absent on pre-QP-208 snapshots.
  depositAmount?: number;
  returnAmount?: number;
  totalCost?: number;
  costEffectPct?: number; // Cumulative reduction-in-return % at END of this period
};

export type CostSnapshot = {
  goalId?: string;
  goalName?: string;
  currency: string;
  costCategorySummaries: CostCategorySummary[];
  firstYearCost?: FirstYearCost; // First year data from yearly_costs
  yearlyCosts?: YearlyCostEntry[]; // Calendar year data for chart
  yearlyCostsRolling?: YearlyCostEntry[]; // Rolling 12-month data for chart
  periodStart: string; // "YYYY-MM" - when investment period starts
  periodEnd: string; // "YYYY-MM" - when investment period ends (COST_SIMULATION_YEARS from start)
  timestamp: string;
};

export type CostChartConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  displayMode?: "rolling" | "calendar"; // Default: "rolling"
  chartType?: "line" | "bar"; // Default: "line"
  // QP-208 — how many years of the simulated series to display (default 10, max 20)
  projectionYears?: number;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// QL-416 — Cost/benefit component (portfolio pages). Style-only: the existing
// side renders whenever the portfolio is a followup; there is no opt-in flag.
export type CostBenefitConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// Expected Value component (works on both global and goal pages like cost_table)
export type ExpectedValueConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  showChart?: boolean; // Default: true
  showSummaryTable?: boolean; // Default: false - Yearly summary
  showYearlyTable?: boolean; // Default: false - Monthly breakdown by year
  compareWithBank?: boolean; // Bank-savings comparison line (default: false)
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type ExpectedValueForecastEntry = {
  date: string; // "YYYY-MM-DD"
  deposit: string;
  returnAmount: string;
  value: string;
  withdrawal: string;
};

export type ExpectedValueSnapshot = {
  goalId?: string;
  goalName?: string;
  currency: string;
  forecast: ExpectedValueForecastEntry[];
  bank?: ExpectedValueForecastEntry[]; // Same cash flows at the flat bank rate (absent on old snapshots)
  bankReturnPct?: number; // Rate the bank series was generated with (legend label)
  periodStart: string; // "YYYY-MM"
  periodEnd: string; // "YYYY-MM"
  timestamp: string;
};

export type ClientClassificationType = "Retail" | "Professional" | "Eligible Counterparty";

export type ClientClassificationSnapshot = {
  classificationType: ClientClassificationType;
  clientType: "Person" | "Company";

  // Person-specific (opt-up)
  personAnswers?: {
    wantsOptUp: boolean;
    hasSignificantTransactions: boolean; // 10+ per quarter
    hasLargePortfolio: boolean; // >500K
    hasFinanceExperience: boolean; // 1+ year
    optUpCriteriaMet: number; // 0-3
  };

  // Company-specific
  companyAnswers?: {
    isFinancialInstitution: boolean;
    hasLargeBalanceSheet: boolean; // >20M
    hasLargeRevenue: boolean; // >40M
    hasLargeEquity: boolean; // >2M
    largeCriteriaMet: number; // 0-3
  };

  determinedAt: string; // ISO timestamp
};

export type ProductAssessmentSnapshot = {
  experiences: Array<{
    productTypeId: string;
    productTypeName: string; // Denormalized for history
    /** Category verdict: plain declaration, or AND across all enabled assessment modes. */
    hasExperience: boolean;
    /** One entry per enabled mode; present when the type has >=1 assessment configured. */
    assessments?: ProductAssessmentOutcome[];
  }>;
  portfolioCollectionId: string;
  portfolioCollectionName: string; // Denormalized for history
  determinedAt: string; // ISO timestamp
};

// ============================================
// FINANCIAL SITUATION TYPES
// ============================================
// Field definitions live in @repo/shared/types (FinancialFieldDefinition).

// Income field types
export type IncomeFieldType =
  | "employment"
  | "self_employment"
  | "pension"
  | "investment"
  | "government"
  | "other";

// Asset field types
export type AssetFieldType = "liquid" | "investment" | "retirement" | "real_estate" | "other";

// Liability field types
export type LiabilityFieldType = "secured" | "unsecured" | "contingent";

// Commitment field types
export type CommitmentFieldType =
  | "housing"
  | "insurance"
  | "dependents"
  | "education"
  | "legal"
  | "other";

// Component configuration
export type FinancialSituationConfig = {
  // Styling
  style?: ComponentStyleType;
  title?: string;
  description?: string;

  // Section visibility (allows splitting across pages)
  showIncome?: boolean; // Show income fields (default: true)
  showAssets?: boolean; // Show asset fields (default: true)
  showLiabilities?: boolean; // Show liability fields (default: true)
  showCommitments?: boolean; // Show commitment fields (default: true)
  // QL-266 — the capacity tab is a section too: hide it on the components that
  // carry other sections when the flow splits the component across pages.
  showCapacity?: boolean; // Show the investment capacity section (default: true)

  // Display options (can override model defaults)
  showStabilityQuestion?: boolean; // Ask about income stability (default: from model)
  // QL-266 — the model-gated expecting-changes question renders on every
  // component unless turned off here; keep it on exactly one when splitting.
  showExpectingChangesQuestion?: boolean; // Ask about expected changes (default: true)
  showCapacityClassification?: boolean; // Show calculated capacity (default: from model)
  showSummaryMetrics?: boolean; // Show derived ratios (default: from model)

  conditionalDisplay?: ConditionalDisplayConfig;
};

export type SumsubVerificationConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  livenessVerification?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type EninLookupConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// QP-179 — both sustainability components are style-only.
export type SustainabilityConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

export type SustainabilityStatusConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  /**
   * QP-179 — when true (default), the advisor can accept a deviation
   * (Accept / Do-not-proceed controls). When false, no deviation controls are
   * shown; a deviation must be resolved by changing preferences or the
   * portfolio. Never affects the public surface (never shows controls).
   */
  allowOverride?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// QP-224 — client preferences component is style-only.
export type ClientPreferencesConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

/**
 * QL-13 — the quiz is authored here, in the component's own config. The
 * component's id is the quiz id used by the runtime, `__quiz_result_<id>`
 * rules and the PDF result field.
 *
 * `questions` is optional so a freshly added, not-yet-filled-in component still
 * type-checks; every reader treats "no questions" as "asks nothing".
 */
export type QuizConfig = {
  questions?: QuizQuestion[];
  /** Pass bar — the summed score of the drawn questions must reach this. */
  minScore?: number;
  /** How many questions each attempt draws at random. */
  questionsToAsk?: number;
  /** Attempts before lockout. Null/absent = unlimited. */
  maxAttempts?: number | null;
  /** Days after the last attempt when attempts return. Null/absent = never. */
  attemptResetDays?: number | null;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// QP-172 — portfolio list with payment details
export type PortfolioListConfig = {
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  showAmount?: boolean;
  showFrequency?: boolean;
  showDates?: boolean;
  conditionalDisplay?: ConditionalDisplayConfig;
};

// Income stability options
export type IncomeStabilityType = "permanent" | "temporary" | "variable";

// Capacity for loss classification
export type CapacityForLossType = "very_low" | "low" | "moderate" | "high" | "very_high";

// Full snapshot stored in FlowInstanceValue
export type FinancialSituationSnapshot = {
  // Entry items (dynamic list with multiple items per type)
  incomeEntries?: FinancialEntryItem[];
  assetEntries?: FinancialEntryItem[];
  liabilityEntries?: FinancialEntryItem[];
  commitmentEntries?: FinancialEntryItem[];

  // Income stability (if enabled)
  incomeStability?: IncomeStabilityType;

  // Calculated totals (by type)
  totals: {
    // Income
    totalAnnualIncome: number;
    incomeByType: Record<string, number>;

    // Assets
    totalAssets: number;
    assetsByType: Record<string, number>;

    // Liabilities
    totalLiabilities: number;
    totalMonthlyDebtPayments: number;
    liabilitiesByType: Record<string, number>;

    // Commitments
    totalMonthlyCommitments: number;
    commitmentsByType: Record<string, number>;
  };

  // Derived metrics (QL-39 — the type-neutral ratios only)
  metrics: {
    netWorth: number;
    debtToIncomeRatio: number;
    monthlyDebtServiceRatio: number;
    savingsRate: number;
  };

  // Metadata
  completedAt: string;
  lastUpdatedAt: string;
  currency: string;
};

// Field scope validation helpers
export function isFieldAllowedOnPage(fieldScope: FlowFieldScope, pageType: PageType): boolean {
  if (fieldScope === "portfolio") return pageType === "portfolio";
  if (fieldScope === "goal") return pageType === "goal" || pageType === "portfolio";
  // Global fields can be added to any page
  return true;
}

// Re-export for API compatibility
export type FlowWithDetails = FlowWithRelations;
